export const RADIO_LISTENER_COUNT_INTERVAL_MS = 30 * 1000

type Listener = (count: number | null) => void
type Deps = {
    fetchCount: () => Promise<number | null>
    intervalMs?: number
    setTimer?: (fn: () => void, ms: number) => unknown
    clearTimer?: (id: unknown) => void
    isVisible?: () => boolean
    onVisible?: (fn: () => void) => () => void
}

// One poll per tab for the sidebar's "listening now" count. It reads a
// count-only endpoint, so showing the number never counts as listening and
// never builds the station's full now-playing payload. Hidden tabs skip polls.
export function createRadioListenerCount(deps: Deps) {
    const intervalMs = deps.intervalMs ?? RADIO_LISTENER_COUNT_INTERVAL_MS
    const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
    const clearTimer = deps.clearTimer ?? (id => clearTimeout(id as any))
    const isVisible =
        deps.isVisible ??
        (() => typeof document === 'undefined' || document.visibilityState !== 'hidden')
    const onVisible =
        deps.onVisible ??
        (fn => {
            if (typeof document === 'undefined') return () => {}
            const handler = () => {
                if (document.visibilityState !== 'hidden') fn()
            }
            document.addEventListener('visibilitychange', handler)
            return () => document.removeEventListener('visibilitychange', handler)
        })
    const listeners = new Set<Listener>()
    let latest: number | null = null
    let timer: unknown = null
    let generation = 0
    let stopVisibility: (() => void) | undefined

    const schedule = () => {
        if (timer != null) clearTimer(timer)
        timer = listeners.size ? setTimer(() => void read(), intervalMs) : null
    }

    function read() {
        if (!isVisible()) {
            schedule()
            return Promise.resolve(latest)
        }
        const requestGeneration = generation
        return deps
            .fetchCount()
            .catch(() => null)
            .then(count => {
                if (requestGeneration !== generation) return latest
                // A failed read keeps the last count rather than blanking it.
                if (count != null) {
                    latest = count
                    listeners.forEach(listener => {
                        try {
                            listener(latest)
                        } catch {}
                    })
                }
                schedule()
                return latest
            })
    }

    return {
        subscribe(listener: Listener) {
            listeners.add(listener)
            if (latest != null) listener(latest)
            if (listeners.size === 1) {
                stopVisibility = onVisible(() => void read())
                void read()
            }
            return () => {
                listeners.delete(listener)
                if (listeners.size) return
                generation++
                if (timer != null) clearTimer(timer)
                timer = null
                latest = null
                stopVisibility?.()
            }
        },
    }
}

function radioListenersUrl() {
    const gonUrl = typeof globalThis !== 'undefined' ? globalThis.gon?.api?.radioListeners : null
    return gonUrl || '/api/v1/radio/listeners'
}

export const radioListenerCount = createRadioListenerCount({
    fetchCount: () =>
        globalThis
            .fetch(radioListenersUrl(), {
                method: 'GET',
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            })
            .then(response => (response?.ok ? response.json() : null))
            .then(data => {
                const count = data?.listener_count
                return count != null && Number.isFinite(Number(count)) ? Number(count) : null
            }),
})

export const subscribeRadioListenerCount = (listener: Listener) =>
    radioListenerCount.subscribe(listener)
